import React, { useState, useEffect, useMemo } from "react";
import { Helmet } from "react-helmet";
import PageLayout, { PageSection } from "../../components/navigation/PageLayout";
import Pagination from "../../components/ui/Pagination";
import Icon from "../../components/AppIcon";
import Input from "../../components/ui/Input";
import Button from "../../components/ui/Button";
import { useTenantContext } from "../../contexts/TenantContext";
import { useLocationContext } from "../../contexts/LocationContext";
import { transactionsApi } from "../../services/api/transactions";

const DEFAULT_PAGE_SIZE = 25;

const Transactions = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notification, setNotification] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Pagination state
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
  });

  // Filter state
  const getDefaultDateRange = () => {
  const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 30);
    return {
   startDate: startDate.toISOString().split("T")[0],
      endDate: endDate.toISOString().split("T")[0],
    };
  };

  const [filters, setFilters] = useState({
    ...getDefaultDateRange(),
    source: "all", // all, sync, manual
 paymentType: "all", // all, cash, card, mixed
  });

  const { selectedTenant, needsTenantSelection, isGlobalAdmin } = useTenantContext();
  const { currentLocation, locations } = useLocationContext();

  const shouldFetchData = !isGlobalAdmin || selectedTenant;

  // Fetch transactions
  const fetchTransactions = async () => {
    if (!shouldFetchData) return;

    setLoading(true);
    setError(null);

 try {
      const params = {
        fromDate: filters.startDate,
    toDate: filters.endDate,
      };
      
      if (currentLocation?.id) {
  params.locationId = currentLocation.id;
      }
 
      if (filters.source !== "all") {
    params.source = filters.source;
      }
      
      if (filters.paymentType !== "all") {
        params.paymentType = filters.paymentType;
      }

      const response = await transactionsApi.getAll(params);
      
      if (response.success) {
        setTransactions(response.data || []);
      } else {
  setError(response.message || "Failed to fetch transactions");
   }
    } catch (err) {
      setError(err.message || "An error occurred while fetching transactions");
    } finally {
    setLoading(false);
    }
  };

  useEffect(() => {
    fetchTransactions();
  }, [shouldFetchData, currentLocation?.id, filters]);

  // Sort and paginate
  const sortedTransactions = useMemo(() => {
    return [...transactions].sort((a, b) => 
      new Date(b.transactionDate) - new Date(a.transactionDate)
    );
  }, [transactions]);

  const paginatedTransactions = useMemo(() => {
    const startIndex = (pagination.page - 1) * pagination.pageSize;
    return sortedTransactions.slice(startIndex, startIndex + pagination.pageSize);
  }, [sortedTransactions, pagination.page, pagination.pageSize]);

  const totalPages = Math.ceil(sortedTransactions.length / pagination.pageSize);

  // Calculate summary stats
  const summaryStats = useMemo(() => {
    const manual = transactions.filter(t => t.isManualEntry);
    const synced = transactions.filter(t => !t.isManualEntry);
    const cash = transactions.filter(t => t.paymentType === "Cash" || t.paymentType === 0);
    const card = transactions.filter(t => t.paymentType === "Card" || t.paymentType === 1);

    return {
      total: transactions.length,
      totalAmount: transactions.reduce((sum, t) => sum + (t.netAmount || t.amount || 0), 0),
      manual: manual.length,
      manualAmount: manual.reduce((sum, t) => sum + (t.netAmount || t.amount || 0), 0),
      synced: synced.length,
   syncedAmount: synced.reduce((sum, t) => sum + (t.netAmount || t.amount || 0), 0),
      cash: cash.length,
      cashAmount: cash.reduce((sum, t) => sum + (t.netAmount || t.amount || 0), 0),
    card: card.length,
      cardAmount: card.reduce((sum, t) => sum + (t.netAmount || t.amount || 0), 0),
    };
  }, [transactions]);

  // Handle delete
  const handleDelete = async (transaction) => {
    try {
      const response = await transactionsApi.delete(transaction.id);
      
      if (response.success) {
        setNotification({ type: "success", message: "Transaction deleted successfully" });
        fetchTransactions();
    } else {
 setNotification({ type: "error", message: response.message || "Failed to delete transaction" });
      }
    } catch (err) {
      setNotification({ type: "error", message: err.message || "An error occurred" });
    } finally {
 setDeleteConfirm(null);
      setTimeout(() => setNotification(null), 5000);
    }
  };

  // Format helpers
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(date);
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: "GBP",
    }).format(amount || 0);
  };

  const getSourceBadge = (transaction) => {
    if (transaction.isManualEntry) {
      return (
        <span className="badge badge-warning">
        <Icon name="PenLine" size={12} className="mr-1" />
          Manual
        </span>
      );
  }
    return (
      <span className="badge badge-info">
        <Icon name="RefreshCw" size={12} className="mr-1" />
      Synced
      </span>
    );
  };

  const getPaymentTypeBadge = (paymentType) => {
    const typeMap = {
      0: { label: "Cash", icon: "Banknote", color: "success" },
      1: { label: "Card", icon: "CreditCard", color: "primary" },
      2: { label: "Mixed", icon: "Layers", color: "accent" },
      Cash: { label: "Cash", icon: "Banknote", color: "success" },
 Card: { label: "Card", icon: "CreditCard", color: "primary" },
      Mixed: { label: "Mixed", icon: "Layers", color: "accent" },
    };

    const info = typeMap[paymentType] || { label: "Unknown", icon: "HelpCircle", color: "neutral" };

  return (
<span className={`badge badge-${info.color}`}>
        <Icon name={info.icon} size={12} className="mr-1" />
        {info.label}
      </span>
    );
  };

  const canDelete = (transaction) => {
    // Can only delete manual entries that haven't been distributed
    return transaction.isManualEntry && !transaction.isDistributed && !transaction.troncRecordDistributed;
  };

  // Handle filter changes
  const handleFilterChange = (key, value) => {
setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // CSV Export
  const exportToCsv = () => {
    const headers = [
      "Date",
      "Location",
      "Amount",
    "Payment Type",
      "Source",
      "Description",
      "Status",
      "Notes",
    ];

    const rows = sortedTransactions.map((t) => [
      formatDate(t.transactionDate),
      t.locationName || t.location || "Unknown",
      t.netAmount || t.amount || 0,
   typeof t.paymentType === "number" ? ["Cash", "Card", "Mixed"][t.paymentType] : t.paymentType,
    t.isManualEntry ? "Manual" : "Synced",
      t.description || "",
      t.isDistributed ? "Distributed" : "Pending",
      t.notes || "",
    ]);

    const escapeCell = (cell) => {
      const str = (cell ?? "").toString();
      if (str.includes(",") || str.includes('"') || str.includes("\n")) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };

    const csvContent = [headers, ...rows]
      .map((row) => row.map(escapeCell).join(","))
      .join("\r\n");

  const BOM = "\uFEFF";
    const blob = new Blob([BOM + csvContent], { type: "text/csv;charset=utf-8" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `transactions-${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const breadcrumbs = [
  { label: "Dashboard", path: "/dashboard-overview" },
    { label: "Transactions", path: "/transactions" },
  ];

  // Global Admin without tenant
  if (isGlobalAdmin && needsTenantSelection) {
    return (
 <>
        <Helmet>
          <title>Transactions - Rocket Tronc</title>
        </Helmet>
        <PageLayout
          title="Transactions"
          description="View all TRONC transactions."
       breadcrumbs={breadcrumbs}
     >
          <div className="card p-12 text-center">
            <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
      <Icon name="Building2" size={40} className="text-primary" />
  </div>
         <h2 className="text-2xl font-bold mb-3">Select a Tenant</h2>
          <p className="text-muted-foreground mb-6 max-w-md mx-auto">
  As a Global Administrator, please select a tenant from the sidebar
   to view transactions.
   </p>
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
   <Icon name="ArrowLeft" size={16} />
  <span>Use the tenant selector in the sidebar</span>
            </div>
     </div>
 </PageLayout>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Transactions - {selectedTenant?.name || "Rocket Tronc"}</title>
     <meta name="description" content="View and manage TRONC transactions" />
      </Helmet>

      <PageLayout
        title="Transactions"
        description={
          selectedTenant
            ? `View all TRONC transactions for ${selectedTenant.name}`
       : "View and manage all TRONC transactions including synced and manual entries"
        }
        breadcrumbs={breadcrumbs}
        actions={
          <Button variant="outline" onClick={fetchTransactions} disabled={loading}>
   <Icon name="RefreshCw" size={16} className={loading ? "animate-spin" : ""} />
            <span className="ml-2">Refresh</span>
   </Button>
        }
      >
        {/* Notification */}
        {notification && (
          <div
    className={`mb-6 alert ${notification.type === "error" ? "alert-error" : "alert-success"}`}
          >
      <Icon
        name={notification.type === "error" ? "AlertCircle" : "CheckCircle"}
              size={20}
       />
  <span>{notification.message}</span>
 <button
        onClick={() => setNotification(null)}
   className="ml-auto p-1 hover:bg-white/20 rounded transition-colors"
            >
         <Icon name="X" size={16} />
         </button>
   </div>
        )}

{/* Delete Confirmation Modal */}
        {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-card rounded-xl border border-border p-6 max-w-md w-full">
              <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-full bg-error/10 flex items-center justify-center">
          <Icon name="Trash2" size={24} className="text-error" />
    </div>
     <div>
      <h3 className="font-semibold">Delete Transaction</h3>
  <p className="text-sm text-muted-foreground">This action cannot be undone</p>
      </div>
              </div>
<p className="mb-6 text-muted-foreground">
       Are you sure you want to delete this manual transaction of{" "}
  <strong>{formatAmount(deleteConfirm.netAmount || deleteConfirm.amount)}</strong>?
            </p>
  <div className="flex gap-3">
            <Button
            variant="outline"
         className="flex-1"
         onClick={() => setDeleteConfirm(null)}
      >
                  Cancel
       </Button>
   <Button
 variant="destructive"
     className="flex-1"
             onClick={() => handleDelete(deleteConfirm)}
     >
        Delete
                </Button>
      </div>
       </div>
      </div>
        )}

        {/* Filters */}
        <div className="mb-6 bg-card rounded-xl border border-border p-4">
  <div className="flex flex-wrap items-end gap-4">
          <div className="flex-1 min-w-[150px]">
   <label className="block text-sm font-medium mb-2">Start Date</label>
           <Input
      type="date"
     value={filters.startDate}
     onChange={(e) => handleFilterChange("startDate", e.target.value)}
              max={filters.endDate}
              />
        </div>
        <div className="flex-1 min-w-[150px]">
     <label className="block text-sm font-medium mb-2">End Date</label>
           <Input
      type="date"
   value={filters.endDate}
  onChange={(e) => handleFilterChange("endDate", e.target.value)}
min={filters.startDate}
          max={new Date().toISOString().split("T")[0]}
  />
            </div>
         <div className="flex-1 min-w-[120px]">
              <label className="block text-sm font-medium mb-2">Source</label>
           <select
      value={filters.source}
      onChange={(e) => handleFilterChange("source", e.target.value)}
        className="input w-full"
       >
      <option value="all">All Sources</option>
         <option value="sync">Synced Only</option>
     <option value="manual">Manual Only</option>
     </select>
    </div>
            <div className="flex-1 min-w-[120px]">
    <label className="block text-sm font-medium mb-2">Payment Type</label>
          <select
       value={filters.paymentType}
      onChange={(e) => handleFilterChange("paymentType", e.target.value)}
    className="input w-full"
              >
  <option value="all">All Types</option>
             <option value="cash">Cash</option>
          <option value="card">Card</option>
                <option value="mixed">Mixed</option>
              </select>
  </div>
            <div className="flex gap-2">
<Button
   variant="outline"
           size="sm"
           onClick={() => {
      const today = new Date().toISOString().split("T")[0];
         setFilters(prev => ({ ...prev, startDate: today, endDate: today }));
    }}
     >
       Today
  </Button>
              <Button
  variant="outline"
      size="sm"
            onClick={() => {
      const endDate = new Date().toISOString().split("T")[0];
          const startDate = new Date();
       startDate.setDate(startDate.getDate() - 7);
             setFilters(prev => ({ ...prev, startDate: startDate.toISOString().split("T")[0], endDate }));
                }}
       >
      Last 7 Days
  </Button>
            </div>
  </div>
 </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="card p-4">
      <p className="text-sm text-muted-foreground mb-1">Total Transactions</p>
            <p className="text-2xl font-bold">{summaryStats.total}</p>
        <p className="text-lg text-primary">{formatAmount(summaryStats.totalAmount)}</p>
   </div>
          <div className="card p-4">
      <p className="text-sm text-muted-foreground mb-1">Synced</p>
        <p className="text-2xl font-bold text-info">{summaryStats.synced}</p>
    <p className="text-lg">{formatAmount(summaryStats.syncedAmount)}</p>
 </div>
        <div className="card p-4">
      <p className="text-sm text-muted-foreground mb-1">Manual Entries</p>
         <p className="text-2xl font-bold text-warning">{summaryStats.manual}</p>
            <p className="text-lg">{formatAmount(summaryStats.manualAmount)}</p>
    </div>
          <div className="card p-4">
            <p className="text-sm text-muted-foreground mb-1">Cash / Card</p>
            <p className="text-lg">
              <span className="text-success">{formatAmount(summaryStats.cashAmount)}</span>
           {" / "}
     <span className="text-primary">{formatAmount(summaryStats.cardAmount)}</span>
        </p>
          </div>
        </div>

        {/* Loading State */}
      {loading && (
    <div className="card p-8 text-center">
            <div className="spinner mx-auto mb-4" />
            <p className="text-muted-foreground">Loading transactions...</p>
        </div>
        )}

      {/* Error State */}
        {error && !loading && (
          <div className="alert alert-warning mb-6">
            <Icon name="AlertTriangle" size={20} />
  <div>
              <p className="alert-title">Unable to load transactions</p>
              <p className="alert-description">{error}</p>
   </div>
     <button className="btn btn-outline btn-sm ml-auto" onClick={fetchTransactions}>
      <Icon name="RefreshCw" size={16} />
              Retry
            </button>
</div>
  )}

        {/* Transactions Table */}
        {!loading && !error && (
          <PageSection
      title="All Transactions"
     description="TRONC transactions from synced systems and manual entries"
        >
            {transactions.length === 0 ? (
            <div className="card p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
   <Icon name="Receipt" size={32} className="text-muted-foreground" />
        </div>
         <h3 className="text-lg font-semibold mb-2">No transactions found</h3>
          <p className="text-muted-foreground">
       Try adjusting your filters or date range.
    </p>
           </div>
            ) : (
          <>
          <div className="card">
   <div className="responsive-table-container">
     <table className="table">
         <thead>
       <tr>
   <th>Date</th>
      <th>Location</th>
          <th>Amount</th>
     <th>Payment Type</th>
         <th>Source</th>
       <th>Status</th>
     <th>Description</th>
      <th className="text-right">Actions</th>
         </tr>
     </thead>
              <tbody>
         {paginatedTransactions.map((transaction) => (
      <tr key={transaction.id}>
      <td>
         <div className="flex items-center gap-2">
        <Icon name="Calendar" size={16} className="text-muted-foreground" />
          <span className="font-medium">
    {formatDate(transaction.transactionDate)}
              </span>
          </div>
      </td>
     <td>
     <div className="flex items-center gap-2">
    <Icon name="MapPin" size={16} className="text-muted-foreground" />
              <span>{transaction.locationName || transaction.location || "Unknown"}</span>
           </div>
       </td>
       <td>
     <span className="font-semibold text-primary">
         {formatAmount(transaction.netAmount || transaction.amount)}
   </span>
          </td>
             <td>{getPaymentTypeBadge(transaction.paymentType)}</td>
     <td>{getSourceBadge(transaction)}</td>
      <td>
  {transaction.isDistributed || transaction.troncRecordDistributed ? (
            <span className="badge badge-success">
   <Icon name="CheckCircle" size={12} className="mr-1" />
            Distributed
   </span>
) : (
       <span className="badge badge-warning">
          <Icon name="Clock" size={12} className="mr-1" />
          Pending
                </span>
    )}
      </td>
  <td>
     <span className="text-sm text-muted-foreground truncate max-w-[200px] block">
           {transaction.description || transaction.notes || "�"}
            </span>
        </td>
             <td className="text-right">
           <div className="flex items-center justify-end gap-2">
           {canDelete(transaction) ? (
 <button
    onClick={() => setDeleteConfirm(transaction)}
        className="btn btn-sm btn-ghost text-error"
    title="Delete manual entry"
            >
   <Icon name="Trash2" size={16} />
                  </button>
      ) : (
             <span className="text-muted-foreground" title="Cannot delete - transaction is locked or synced">
        <Icon name="Lock" size={14} />
     </span>
            )}
        </div>
        </td>
         </tr>
          ))}
            </tbody>
        </table>
        </div>
         </div>

      <Pagination
     page={pagination.page}
          pageSize={pagination.pageSize}
       totalCount={sortedTransactions.length}
             totalPages={totalPages}
         onPageChange={(page) => setPagination(prev => ({ ...prev, page }))}
                  onPageSizeChange={(pageSize) => setPagination({ page: 1, pageSize })}
   onExportAll={exportToCsv}
        exportDisabled={sortedTransactions.length === 0}
            className="mt-4"
        />
          </>
            )}
          </PageSection>
      )}
      </PageLayout>
    </>
  );
};

export default Transactions;
